import React from 'react';
import styled from 'styled-components';

// Page Layout Components
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

export const Container = styled.div`
  padding: 30px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const HeaderLeft = styled.div``;

export const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

export const BackButtonWrapper = styled.div`
  margin-bottom: 16px;

  button {
    width: auto;
    padding: 0;
    background: none;
    border: none;
    box-shadow: none;

    &:hover {
      background: none;
      box-shadow: none;
    }
  }
`;

export const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

export const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0;
  line-height: 1.6;
`;

export const HeaderButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 24px;
  }
`;

// Step Navigation Components
export const StepperContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`;

export const StepperTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${({ isClickable }) => (isClickable ? 0.8 : 1)};
  }
`;

export const StepCircle = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  transition: all 0.2s;
  color: white;

  background: ${({ status, theme }) =>
    status === 'completed' || status === 'active'
      ? theme.colors.primary
      : theme.name === 'dark'
      ? '#374151'
      : '#E5E7EB'};

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
`;

export const StepLabel = styled.span`
  margin-left: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ status, theme }) =>
    status === 'active' || status === 'completed'
      ? theme.name === 'dark'
        ? '#ffffff'
        : theme.colors.blueBlack
      : theme.name === 'dark'
      ? '#64748B'
      : '#9CA3AF'};

  @media (max-width: 640px) {
    display: none;
  }
`;

export const StepConnector = styled.div`
  width: 100px;
  height: 2px;
  background: ${({ completed, theme }) =>
    completed
      ? theme.colors.primary
      : theme.name === 'dark'
      ? '#374151'
      : '#E5E7EB'};
  margin: 0 12px;

  @media (max-width: 768px) {
    width: 80px;
  }

  @media (max-width: 480px) {
    width: 60px;
    margin: 0 10px;
  }
`;

export const StepNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.card.border};
`;

export const NavButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

// Grid Layout Components
export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Form Card Components
export const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '24px')};
`;

export const FormSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const SectionDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: -8px 0 16px 0;
  line-height: 1.5;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr'};
  gap: 16px;
  margin-bottom: 16px;
  overflow: visible;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  overflow: visible;
  position: relative;
`;

// Form Input Components
export const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  margin-bottom: 8px;
`;

export const LabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  margin-bottom: 8px;
`;

// Form field styled components (for backward compatibility)
// New code should import Input, Textarea, Select from 'components/FormFields' for built-in label support
export const RequiredBadge = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.status.error};
  margin-left: 6px;
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.colors.input?.background || theme.colors.card.background};
  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.colors.status.error : theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryOpacity(0.1)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textGray.muted};
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.colors.input?.background || theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryOpacity(0.1)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textGray.muted};
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  padding-right: 48px;
  background: ${({ theme }) =>
    theme.colors.input?.background || theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s;
  cursor: pointer;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 18px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryOpacity(0.1)};
  }

  option {
    background: ${({ theme }) => theme.colors.background.primary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const ErrorText = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.error};
  margin-top: 6px;
`;

// Toggle Components
export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.card.border};

  &:last-child {
    border-bottom: none;
  }
`;

export const ToggleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

export const ToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ToggleTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ToggleDescription = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
`;

// Sales Windows Components
export const SalesWindowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SalesWindowCard = styled.div`
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 16px;
  position: relative;
`;

export const SalesWindowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const SalesWindowTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.dark};
`;

export const SmallIconButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

export const AddWindowButtonWrapper = styled.div`
  button {
    width: 100%;
    padding: 12px 20px;
    border-style: dashed;
  }
`;

export const EmptyWindowsMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 24px;
  margin: 0;
`;

// Tiered Pricing Components
export const TierSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.card.border};
`;

export const TierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const TierTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const AddTierButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 6px 12px;
    font-size: 13px;
  }
`;

export const TierRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 8px;
`;

export const EmptyTiersMessage = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 20px;
  margin: 0;
  background: ${({ theme }) => theme.colors.background.overlay};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.card.border};
`;

export const RemoveTierButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

// Pages Section Components
export const PagesContainer = styled.div`
  display: flex;
  gap: 40px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

export const PagesListSection = styled.div`
  flex: 2;
`;

export const PagesOrderSection = styled.div`
  flex: 1;
  min-width: 250px;

  @media (max-width: 900px) {
    order: -1;
  }
`;

export const PageCard = styled.div`
  background: ${({ theme }) => theme.colors.background.overlay};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 20px;
  margin-bottom: 16px;
  position: relative;
`;

export const PageCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const PageCardTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray.dark};
`;

export const DeletePageButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

export const AddPageButtonWrapper = styled.div`
  button {
    width: 100%;
    padding: 14px 24px;
  }
`;

export const EmptyPagesMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  text-align: center;
  padding: 40px 24px;
  margin: 0 0 16px 0;
  background: ${({ theme }) => theme.colors.background.overlay};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.card.border};
`;

export const OrderSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const DraggablePageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.card.background};
  border: 1px solid ${({ theme }) => theme.colors.card.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 8px;
  cursor: move;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const DragHandle = styled.div`
  color: ${({ theme }) => theme.colors.textGray.muted};
  display: flex;
  align-items: center;
`;

export const PageOrderName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.dark};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Waiver Components
export const WaiversContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const EmptyText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

export const WaiverRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RemoveWaiverButtonWrapper = styled.div`
  button {
    width: 28px;
    height: 28px;
    padding: 0;
  }
`;

export const AddWaiverButtonWrapper = styled.div`
  margin-top: 8px;

  button {
    width: auto;
    padding: 8px 16px;
    font-size: 13px;
  }
`;

// Icons
export const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

export const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const DragHandleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

export const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

export const MinusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" stroke="currentColor" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

// Loading and Error States
export const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-top-color: #fa4616;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin-top: 16px;
`;

export const ErrorCard = styled.div`
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(239, 68, 68, 0.05)'};
  border: 1px solid
    ${({ theme }) =>
      theme.name === 'dark'
        ? 'rgba(239, 68, 68, 0.3)'
        : 'rgba(239, 68, 68, 0.2)'};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  color: #ef4444;
`;

export const ExpandedContent = styled.div`
  display: ${({ show }) => (show ? 'block' : 'none')};
  padding: 16px 0 8px 0;
`;

// Wrapper for remove window button (small icon danger button)
export const RemoveWindowButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 6px;
  }
`;

// Platform detection
export const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

// Ticket types available based on platform
export const TICKET_TYPES = [
  { value: 'ticket', label: 'Ticket' },
  { value: 'other', label: 'Other' },
  // Registration is only available for PitPay (not TicketHoss)
  ...(!isTicketHoss ? [{ value: 'registration', label: 'Registration' }] : []),
];

// Annual ticket types (for Annual/Season tickets)
export const ANNUAL_TICKET_TYPES = [
  { value: 'annual', label: 'Annual' },
  { value: 'membership', label: 'Membership' },
];

export const VISIBILITY_OPTIONS = [
  { value: 'visible', label: 'App & POS' },
  { value: 'app_only', label: 'App Only' },
  { value: 'pos_only', label: 'POS Only' },
  { value: 'hidden', label: 'Hidden' },
];

// Default fee calculation based on ticket type and platform
export const getDefaultTicketFees = (price, ticketType) => {
  let fee_dollar, fee_percent;
  const numPrice = parseFloat(price) || 0;

  if (isTicketHoss) {
    if (ticketType === 'other') {
      // "Other" tickets: $0 + 5.25%
      if (numPrice >= 1) {
        fee_dollar = 0;
        fee_percent = 5.25;
      }
    } else if (numPrice >= 1 && numPrice <= 5) {
      fee_dollar = 0.75;
      fee_percent = 3.25;
    } else if (numPrice > 5) {
      fee_dollar = 1.25;
      fee_percent = 3.25;
    }
  } else {
    // PitPay platform
    if (ticketType === 'other') {
      if (numPrice >= 1 && numPrice <= 500) {
        fee_dollar = 0;
        fee_percent = 5.25;
      } else if (numPrice > 500) {
        fee_dollar = 10;
        fee_percent = 3.25;
      }
    } else {
      // Regular ticket type
      if (numPrice >= 1 && numPrice <= 1000000) {
        fee_dollar = 1.85;
        fee_percent = 3.25;
      }
    }
  }

  return { fee_dollar, fee_percent };
};

// Default POS fee calculation based on platform
export const getDefaultPosFees = (price) => {
  let pos_fee_dollar, pos_fee_percent;
  const numPrice = parseFloat(price) || 0;

  if (isTicketHoss) {
    // Non-reserved seat defaults for templates
    pos_fee_dollar = 0;
    pos_fee_percent = 5.25;
  } else {
    // PitPay platform
    if (numPrice >= 1 && numPrice <= 1000000) {
      pos_fee_dollar = 0;
      pos_fee_percent = 5.25;
    }
  }

  return { pos_fee_dollar, pos_fee_percent };
};
